import React, { useEffect, useState } from 'react'
import '../common.css'
import { DataGrid } from '@mui/x-data-grid'
import { Typography, TextField, Tooltip, Alert } from '@mui/material'
import { ApiService } from '@carrier-ecat/workflowui-globalfunctions'
import ImportButton from '../importButton/importButton'
import ExportButton from '../exportButton/exportButton'

import { apiUrl } from '../../../environments/environments'
import Warning from '@mui/icons-material/Warning'
import SyncPriceButton from '../../global/SyncPriceButton'
import { useIsProjectEditor } from '../../../hooks/useUserPermissions'

const REQUIRED_FIELDS = ['productType', 'size', 'assembly', 'varcode']

const getMissingFields = (row) => REQUIRED_FIELDS.filter((f) => !row[f])

const Header = ({ title, onFilter, field, filterValue, id }) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant='caption' style={{ fontSize: '1rem' }}>
            {title}
        </Typography>
        <TextField
            variant='standard'
            value={filterValue || ''}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onFilter(field, e.target.value)}
            style={{ width: '100%' }}
            inputProps={{ id }}
        />
    </div>
)

function QmPage() {
    const [data, setData] = useState([])
    const [dataToDisplay, setDataToDisplay] = useState([])
    const [loading, setLoading] = useState(true)
    const [filters, setFilters] = useState({})
    const [importWarning, setImportWarning] = useState(null)
    const isProjectEditor = useIsProjectEditor()

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }))
    }

    const refreshData = async () => {
        setLoading(true)
        try {
            const { status, data } = await ApiService(`${apiUrl}/rooftop/qm/`)
            if (status === 200 && data) {
                setData(data.result)
            }
        } catch (error) {
            console.error('Error loading QM prices:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        refreshData()
    }, [])

    useEffect(() => {
        let filtered = [...data]

        Object.entries(filters).forEach(([field, value]) => {
            const raw = value == null ? '' : String(value).trim()
            if (raw === '') return

            filtered = filtered.filter((row) => {
                const fieldValue = row[field]

                if (field === 'price') {
                    if (fieldValue == null || fieldValue === '') return raw === '-' || raw === ''
                    return String(fieldValue).includes(raw)
                }

                if (fieldValue == null || fieldValue === '') return false
                return String(fieldValue).toLowerCase().includes(raw.toLowerCase())
            })
        })

        setDataToDisplay(filtered)
    }, [data, filters])

    const uploadSuccess = ({ result, skipped }) => {
        setData(result)
        setImportWarning(skipped > 0 ? `${skipped} row(s) skipped: missing productType, size, assembly, or varcode.` : null)
        setLoading(false)
    }

    const columns = [
        {
            field: '_status',
            headerName: '',
            width: 44,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const missing = getMissingFields(params.row)
                if (missing.length === 0) return null
                return (
                    <Tooltip title={`Missing: ${missing.join(', ')}`}>
                        <Warning color='warning' fontSize='small' />
                    </Tooltip>
                )
            },
        },
        {
            field: 'varcond',
            headerName: 'Varcond',
            flex: 1,
            minWidth: 120,
            renderHeader: () => (
                <Header
                    title='Varcond'
                    onFilter={handleFilterChange}
                    field='varcond'
                    filterValue={filters.varcond}
                    id='filter-qm-varcond'
                />
            ),
        },
        {
            field: 'productType',
            headerName: 'Product Type',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Product Type'
                    onFilter={handleFilterChange}
                    field='productType'
                    filterValue={filters.productType}
                    id='filter-qm-producttype'
                />
            ),
        },
        {
            field: 'size',
            headerName: 'Size',
            flex: 1,
            minWidth: 120,
            renderHeader: () => (
                <Header
                    title='Size'
                    onFilter={handleFilterChange}
                    field='size'
                    filterValue={filters.size}
                    id='filter-qm-size'
                />
            ),
        },
        {
            field: 'assembly',
            headerName: 'Assembly',
            flex: 1,
            minWidth: 130,
            renderHeader: () => (
                <Header
                    title='Assembly'
                    onFilter={handleFilterChange}
                    field='assembly'
                    filterValue={filters.assembly}
                    id='filter-qm-assembly'
                />
            ),
        },
        {
            field: 'varcode',
            headerName: 'Varcode',
            flex: 1,
            minWidth: 120,
            renderHeader: () => (
                <Header
                    title='Varcode'
                    onFilter={handleFilterChange}
                    field='varcode'
                    filterValue={filters.varcode}
                    id='filter-qm-varcode'
                />
            ),
        },
        {
            field: 'price',
            headerName: 'Price',
            flex: 1,
            minWidth: 130,
            renderCell: (params) => {
                if (params.value === undefined || params.value === null) {
                    return (
                        <span style={{ color: 'red', background: 'white' }}>
                            <Warning color='error' className='warningIcon' /> No Price
                        </span>
                    )
                }
                return params.value
            },
            renderHeader: () => (
                <Header
                    title='Price'
                    onFilter={handleFilterChange}
                    field='price'
                    filterValue={filters.price}
                    id='filter-qm-price'
                />
            ),
        },
    ]

    return (
        <div className='page-container'>
            <div className='actions'>
                {isProjectEditor && (
                    <>
                        <div>
                            <ImportButton
                                api='/rooftop/qm/'
                                onSuccess={uploadSuccess}
                                onError={() => setLoading(false)}
                                onStart={() => setLoading(true)}
                            />
                        </div>
                        <div>
                            <ExportButton
                                id='download-prices-qm'
                                data={data}
                                columns={columns}
                                fileName='QM_Prices.xlsx'
                                sheetName='QM_Prices'
                            />
                        </div>
                        <div>
                            <SyncPriceButton
                                builder={'rooftops'}
                                onSyncComplete={refreshData}
                                initialMessage='Sync all rooftops prices'
                            />
                        </div>
                        <div>{loading && <span> LOADING .... </span>}</div>
                    </>
                )}
            </div>

            <div className='table-container' style={{ marginBottom: '20px' }}>
                <Typography variant='h6' component='h2' gutterBottom>
                    QM Prices
                </Typography>
                {importWarning && (
                    <Alert severity='warning' onClose={() => setImportWarning(null)} style={{ marginBottom: '12px' }}>
                        {importWarning}
                    </Alert>
                )}

                <div>
                    <DataGrid
                        autoHeight={false}
                        style={{
                            backgroundColor: 'white',
                            width: '100%',
                            fontSize: '1rem',
                            height: '100%',
                            '.MuiDataGridOverlayWrapper': { minHeight: '100px' },
                        }}
                        rows={dataToDisplay}
                        columns={columns}
                        getRowId={(row) => `${row.productType}-${row.size}-${row.assembly}-${row.varcode}`}
                        loading={loading}
                        pageSizeOptions={[5, 10, 20]}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                        rowHeight={70}
                    />
                </div>
            </div>
        </div>
    )
}

export default QmPage
